import { FC, useRef, useMemo, useEffect, } from "react";
// 组件
import { PlusOutlined } from "@ant-design/icons";
import { Table } from "antd";
import { ProTable } from '../antd';
import TableRow from "./subpage/row";
import TableCell from "./subpage/cell";
import TotalTable, { TotalTableActionType } from "./subpage/totalTable";
// 方法
import MianSubTableContext from './context/mianSubTableContext';
import useEditSubTablehook from './hook/useEditSubTablehook';
import columnsHook from './hook/columnsHook';
import classNames from "classnames";
import { filterDeleteData, formatRowKey, getEffectiveChildren } from "./util";
import { filterEditObjEmptyValue } from "../utils/utils";
import { useLayoutContext } from "../PageLayout/context/LayoutContext";
import { getRecordCode } from "../EdiTableCopy/utils";
// 常量
import { IObject } from "../constants/interface";
import { EditMainSubTablePrope, MAIN_ROW_KEY, RowFormProps } from "./interface";
import { STATUS_CREATED_CODE, STATUS_CREATED } from "../constants/constant";
import { FormInstance } from "antd/lib/form";
import './style.less';
/** 可编辑主子表 建议使用EditMainSubTableNew */
const EditMainSubTable: FC<EditMainSubTablePrope> = props => {
  const {
    height, dataSource, defaultExpandAllRows, className, bordered, disabled, style, showAddSubRow, showEmptySubRow,
    actionRef, totalKeys, totalIndex, totalText, onValuesChange, hiddenAddSubRow, formatTotalData,
  } = props;
  const { enumData } = useLayoutContext();
  const tableRef = useRef<HTMLDivElement>(null);
  const forms = useRef<RowFormProps[]>([]);
  const flagIndex = useRef<number>(0);
  const totalTableRef = useRef<TotalTableActionType>();
  const { tableDataSource, expandedRowKeys, dispatchEditSubTable, } = useEditSubTablehook(flagIndex, dataSource, forms, defaultExpandAllRows, showAddSubRow, showEmptySubRow);
  const { tableColumns } = columnsHook(props, dispatchEditSubTable, tableDataSource, enumData, forms,);
  /** 可编辑主子表格展示的数据 */
  const editMainSubTableDataSource = useMemo(() => {
    const newDataSource = filterDeleteData(tableDataSource, hiddenAddSubRow);
    return newDataSource;
  }, [tableDataSource]);
  useEffect(() => {
    handleUpdateTotalData();
  }, [tableDataSource]);
  /** 表单字段值改变 */
  const handleValuesChange = (
    value: any,
    name: any,
    rowValues: any,
    key: string,
    form: FormInstance,
    record: any,
  ) => {
    // 是否需要更新总计
    if (Array.isArray(totalKeys) && totalKeys.length) {
      const findItem = totalKeys.find(v => v.dataIndex === name);
      if (findItem) {
        handleUpdateTotalData();
      }
    }
    onValuesChange?.(value, name, rowValues, key, form, record);
  };
  /** 主表展开、合并 */
  const handleExpand = (expanded: boolean, record: any) => {
    if (expanded) {
      dispatchEditSubTable('addExpandedRowKeys', {updateExpandedRowKeys: record.key});
    } else {
      dispatchEditSubTable('deleteExpandedRowKeys', {updateExpandedRowKeys: record.key});
    }
  };
  /** 添加主表行 */
  const handleMainAdd = (addItem?: IObject | IObject[]) => {
    dispatchEditSubTable('addRow', {addItem});
  };
  /** 添加子表行 */
  const handleChildrenAdd = (mainKey: string, addItem?: IObject | IObject[]) => {
    dispatchEditSubTable('addChildren', { mainKey, addItem });
  };
  /** 追加主子表数据 */
  const handleAppend = (appendItem: IObject[]) => {
    dispatchEditSubTable('append', {appendItem});
  }
  /** 覆盖主子表数据 */
  const handleCover = (addItem?: IObject[]) => {
    dispatchEditSubTable('cover', {addItem});
  }
  /** 删除 */
  const handleDelete = (record: any) => {
    const { mainKey, childrenKey } = formatRowKey(record.key);
    if (mainKey && childrenKey) {
      dispatchEditSubTable('deleteChildren', {mainKey, childrenKey: record.key});
      return;
    }
    if (mainKey && !childrenKey) {
      dispatchEditSubTable('deleteRow', {mainKey});
    }
  };
  /** 提交 */
  const handleSubmit = async () => {
    const newDataSource: any[] = [];
    let flag = true;
    // 判断主子表是否有数据
    if (Array.isArray(tableDataSource) && tableDataSource.length) {
      for (let i = 0; i < tableDataSource.length; i++) {
        try {
          const row: any = tableDataSource[i] ?? {};
          const { key, [MAIN_ROW_KEY]: isMainTable, ...restRow} = row;
          const rowKey = row.key;
          const rowForm = forms.current?.[rowKey]?.main;
          let newRowItem: any = {};
          if (rowForm) {
            const rowFormData = await rowForm.validateFields();
            const rowChildren = getEffectiveChildren(row.children);
            const childrenForms = forms.current?.[rowKey]?.children;
            let newChildrenItem: any[] = [];
            // 循环子表数据
            for (let n = 0; n < rowChildren.length; n++) {
              const child = rowChildren[n] ?? {};
              const {key, ...restChild} = child;
              const { childrenKey } = formatRowKey(child.key);
              if (childrenKey) {
                const childrenForm = childrenForms?.[Number(childrenKey) | 0];
                if (childrenForm) {
                  const childrenFormData = await childrenForm.validateFields();
                  newChildrenItem.push({
                    ...restChild,
                    ...filterEditObjEmptyValue(childrenFormData),
                    [STATUS_CREATED_CODE]: getRecordCode(
                      restChild[STATUS_CREATED_CODE],
                      childrenFormData[STATUS_CREATED_CODE],
                    ),
                  });
                } else {
                  newChildrenItem.push(restChild);
                }
              } else {
                newChildrenItem.push(restChild);
              }
            }
            newRowItem = {
              ...restRow,
              ...filterEditObjEmptyValue(rowFormData),
              [STATUS_CREATED_CODE]: getRecordCode(
                restRow[STATUS_CREATED_CODE],
                rowFormData[STATUS_CREATED_CODE],
              ),
              children: newChildrenItem,
            };
          } else {
            newRowItem = {...restRow, children: getEffectiveChildren(restRow.children),};
          }
          newDataSource.push(newRowItem);
        } catch (errorInfo) {
          flag = false;
        }
      }
    }
    return flag ? newDataSource : false;
  };
  /** 清空 */
  const handleEmptyMainChildren = (mainKey: string) => {
    dispatchEditSubTable('emptyRow', {mainKey});
  };
  /** 清空 */
  const handleEmpty = () => {
    dispatchEditSubTable('empty');
  };
  /** 替换 */
  const handleChangeDataSource = (data: any[]) => {
    dispatchEditSubTable('changeData', { dataSource: data, });
  };
  /**
   * 返回dataSource
   * @param isFilterDeleteData 是否过滤已删除数据
   * @returns 
   */
  const handleGetDataSource = (isFilterDeleteData?: boolean) => {
    const newDataSource: any[] = [];
    if (Array.isArray(tableDataSource) && tableDataSource.length) {
      tableDataSource.forEach(group => {
        const groupRecordStatus = group[STATUS_CREATED_CODE];
        if (isFilterDeleteData && groupRecordStatus === STATUS_CREATED.DELETE) {
          return;
        }
        const groupKey = group.key;
        const groupForm = forms.current?.[groupKey]?.main;
        const groupFormData = groupForm?.getFieldsValue(true);
        const groupChildren = getEffectiveChildren(group.children);
        const newChildren: any[] = [];
        if (Array.isArray(groupChildren) && groupChildren.length) {
          const childrenForms = forms.current?.[groupKey]?.children;
          groupChildren.forEach(item => {
            const itemRecordStatus = item[STATUS_CREATED_CODE];
            if (isFilterDeleteData && itemRecordStatus === STATUS_CREATED.DELETE) {
              return;
            }
            const { childrenKey } = formatRowKey(item.key);
            const childrenForm = childrenForms?.[Number(childrenKey) | 0];
            const itemFormData = childrenForm?.getFieldsValue(true);
            newChildren.push({
              ...item,
              ...itemFormData,
            });
          });
        }
        newDataSource.push({
          ...group,
          ...groupFormData,
          children: newChildren,
        });
      });
    }
    return newDataSource;
  };
  /** 更新统计数据 */
  const handleUpdateTotalData = () => {
    if (Array.isArray(totalKeys) && totalKeys.length) {
      const effectiveDataSource = handleGetDataSource(true);
      totalTableRef.current?.onChange(effectiveDataSource);
    }
  };
  /** 手动传入表格数据进行统计数据更新 */
  const handleChangeTotal = (tableData?: any[]) => {
    if (tableData) {
      totalTableRef.current?.onChange(tableData);
    } else {
      handleUpdateTotalData();
    }
  };
  /** 更新单行主表表单数据 */
  const handleMainValues = (mainKey: string, values: any) => {
    if (mainKey === undefined) return;
    const formData = forms.current[Number(mainKey)].main;
    formData?.setFieldsValue({
      ...values,
      [STATUS_CREATED_CODE]: STATUS_CREATED.UPDATE,
    });
  };
  /** 更新单行主表下子表的表单数据 */
  const handleMainChildrenValues = (childrenKey: string, values: any) => {
    if (childrenKey === undefined) return;
    const keyArr = formatRowKey(childrenKey);
    const formData = forms.current[Number(keyArr.mainKey)]?.children?.[Number(keyArr.childrenKey)];
    formData?.setFieldsValue({
      ...values,
      [STATUS_CREATED_CODE]: STATUS_CREATED.UPDATE,
    });
  };
  /** 更新单行主表下所有子表的表单数据 */
  const handleMainChildrensValues = (mainKey: string, values: any) => {
    if (mainKey === undefined) return;
    const formDatas = forms.current[Number(mainKey)].children;
    formDatas?.forEach(item => {
      item?.setFieldsValue({
        ...values,
        [STATUS_CREATED_CODE]: STATUS_CREATED.UPDATE,
      });
    });
  };
  const classes = useMemo(() => {
    return classNames('edit-main-sub-table', className);
  }, [className]);
  if (actionRef) {
    actionRef.current = {
      empty: handleEmpty,
      cover: handleCover,
      append: handleAppend,
      delete: handleDelete,
      submit: handleSubmit,
      addMain: handleMainAdd,
      addChildren: handleChildrenAdd,
      changeTotal: handleChangeTotal,
      getDataSource: handleGetDataSource,
      setMainValues: handleMainValues,
      changeDataSource: handleChangeDataSource,
      emptyMainChildren: handleEmptyMainChildren,
      setMainChildrenValues: handleMainChildrenValues,
      setMainChildrensValues: handleMainChildrensValues,
      forms: forms.current,
    };
  }
  return (
    <MianSubTableContext.Provider value={{
      editableRef: tableRef.current || undefined,
      dispatchEditSubTable,
      forms,
      showAddSubRow,
      showEmptySubRow,
    }}>
      <div
        ref={tableRef}
        className={classes}
        style={style}
      >
        <ProTable
          dataSource={editMainSubTableDataSource}
          columns={tableColumns}
          pagination={false}
          bordered={bordered}
          size="small"
          expandable={{
            expandedRowKeys,
            onExpand: handleExpand,
          }}
          components={{
            body: {
              row: TableRow,
              cell: TableCell,
            },
          }}
          scroll={{
            scrollToFirstRowOnChange: true,
            y: height,
          }}
          // @ts-ignore
          onRow={(record: any) => ({
            record,
            disabled,
            onValuesChange: handleValuesChange,
          })}
          summary={() => {
            if (
              Array.isArray(totalKeys) &&
              !!totalKeys.length &&
              Array.isArray(editMainSubTableDataSource) &&
              !!editMainSubTableDataSource.length
            ) {
              return (
                <Table.Summary fixed>
                  <TotalTable
                    actionRef={totalTableRef}
                    columns={tableColumns}
                    totalKeys={totalKeys}
                    // dataSource={ediTableDataSource}
                    totalIndex={totalIndex}
                    totalText={totalText}
                    formatTotalData={formatTotalData}
                  />
                </Table.Summary>
              );
            }
          }}
        />
      </div>
    </MianSubTableContext.Provider>
  );
};

EditMainSubTable.defaultProps = {
  height: 350,
  defaultExpandAllRows: true,
  deleteType: 'ALL',
  addIcon: <PlusOutlined />,
  hideAdd: false,
  disabled: false,
  bordered: true,
  showAddSubRow: false,
  addSubRowText: '添加一行',
  showEmptySubRow: true,
  emptySubRowText: '暂无数据',
  operationWidth: 50,
};

export default EditMainSubTable;
